import { Link, useSearchParams } from "react-router-dom";
import { CalendarPlus, Share2, Heart } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { useFavorites } from "@/lib/favorites";
import { PageHero } from "@/components/common";
import { ActivityRow } from "@/components/ActivityCard";
import useTitle from "@/lib/useTitle";
import { track } from "@/lib/api";
import { dayOf, downloadICS, fmtDay, overlaps } from "@/lib/time";

function SharedAgenda({ slugs }) {
  const site = useSite();
  const { has, toggle } = useFavorites();
  const acts = site.data.activities.filter((a) => slugs.includes(a.slug));
  const [, setParams] = useSearchParams();
  if (!acts.length) return null;
  const addAll = () => { acts.filter((a) => !has(a.id)).forEach(toggle); setParams({}); };
  return (
    <div className="rounded-3xl bg-amber-100 border border-amber-300 p-6 mb-10" data-testid="shared-agenda">
      <p className="font-display text-2xl font-bold">Agenda partilhada consigo</p>
      <ul className="mt-3 space-y-1 text-sm">{acts.map((a) => <li key={a.id}>• {a.title}</li>)}</ul>
      <button onClick={addAll} className="mt-4 h-12 px-5 rounded-full bg-[var(--ink)] text-white font-semibold" data-testid="shared-agenda-add-all">Adicionar todas à minha Feira</button>
    </div>
  );
}

export default function MinhaFeira() {
  useTitle("A minha Feira");
  const site = useSite();
  const { ids } = useFavorites();
  const [params] = useSearchParams();
  const shared = (params.get("partilha") || "").split(",").filter(Boolean);
  const acts = site.data.activities.filter((a) => ids.includes(a.id));
  const conflictsOf = (a) => acts.filter((b) => overlaps(a, b)).map((b) => b.title).join("; ");
  const nConflicts = acts.filter((a) => conflictsOf(a)).length;
  const byDay = acts.reduce((acc, a) => { (acc[dayOf(a.start)] = acc[dayOf(a.start)] || []).push(a); return acc; }, {});

  const share = async () => {
    const url = `${window.location.origin}/a-minha-feira?partilha=${acts.map((a) => a.slug).join(",")}`;
    track("share", "/a-minha-feira");
    if (navigator.share) { try { await navigator.share({ title: "A minha Feira de Saúde e Bem-Estar", url }); return; } catch { return; } }
    await navigator.clipboard.writeText(url);
    toast.success("Ligação da sua agenda copiada");
  };

  return (
    <>
      <PageHero eyebrow="Agenda pessoal" title="A minha Feira" tone="#be123c" image="/img/circle.jpg">As atividades que guardar ficam apenas neste dispositivo. Não precisa de criar conta.</PageHero>
      <div className="container-x py-12">
        {shared.length > 0 && <SharedAgenda slugs={shared} />}
        {acts.length === 0 ? (
          <div className="text-center py-16" data-testid="my-fair-empty">
            <Heart className="h-12 w-12 mx-auto text-[var(--clay)]" aria-hidden />
            <p className="font-display text-2xl font-bold mt-4">Ainda não guardou atividades</p>
            <p className="text-slate-600 mt-2">Toque no coração de uma atividade para a adicionar aqui.</p>
            <Link to="/programacao" className="mt-6 inline-flex h-12 px-6 items-center rounded-full bg-[var(--ink)] text-white font-semibold" data-testid="my-fair-explore-button">Explorar programação</Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <p className="text-lg" data-testid="my-fair-count"><strong>{acts.length}</strong> atividade{acts.length > 1 ? "s" : ""} guardada{acts.length > 1 ? "s" : ""}
                {nConflicts > 0 && <span className="ml-3 rounded-full bg-amber-200 px-3 py-1 text-sm font-semibold text-amber-900" data-testid="my-fair-conflicts">{nConflicts} com horário sobreposto</span>}</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => { downloadICS(acts, site.placeOf, "a-minha-feira"); track("calendar", "/a-minha-feira"); }} className="inline-flex items-center gap-2 h-12 px-5 rounded-full bg-[var(--leaf)] text-white font-semibold" data-testid="my-fair-calendar-button"><CalendarPlus className="h-5 w-5" aria-hidden />Adicionar tudo ao calendário</button>
                <button onClick={share} className="inline-flex items-center gap-2 h-12 px-5 rounded-full border-2 border-black/15 font-semibold" data-testid="my-fair-share-button"><Share2 className="h-5 w-5" aria-hidden />Partilhar agenda</button>
              </div>
            </div>
            {Object.entries(byDay).sort().map(([d, list]) => (
              <section key={d} className="mb-10" aria-label={fmtDay(d)}>
                <h2 className="font-display text-2xl font-extrabold mb-4 capitalize">{fmtDay(d)}</h2>
                <div className="grid gap-3">{list.map((a) => <ActivityRow key={a.id} act={a} conflict={conflictsOf(a)} />)}</div>
              </section>
            ))}
          </>
        )}
      </div>
    </>
  );
}
